import { useId } from "react";
import type { MmRegister } from "../../api/mentalModels";
import { useMmText } from "../../hooks/useMmText";
import { cn } from "../../lib/utils";

const REGISTERS: MmRegister[] = ["plain", "guided", "expert"];

const HINT_FALLBACK: Record<MmRegister, string> = {
  // Matches what the setting really does: the AI's question depth and voice
  // (BE utils/mmPrompts.js REGISTER_LINES), tool names, and which explanations show.
  plain: "Simple: short, easy questions in everyday words, one idea at a time.",
  guided: "Guided: a supportive coach. Questions with a little structure, each tool explained.",
  expert: "Expert: a demanding thinking partner. Deeper, multi-part questions and the technical terms, no cushioning.",
};

interface Props {
  register: MmRegister;
  onChange: (r: MmRegister) => void;
  className?: string;
}

/**
 * How the guide's questions and tips are worded: a quiet line with the three
 * choices and one small line saying what the current one means (it used to hide
 * in the page's "…" menu). Each option's hint is also its tooltip.
 */
const MmVoiceSwitch = ({ register, onChange, className }: Props) => {
  const { tm } = useMmText(register);
  const labelId = useId();
  const hintId = useId();
  return (
    <div className={cn("space-y-0.5 text-xs text-muted-foreground", className)}>
      <div className="flex flex-wrap items-center gap-x-1">
        <span id={labelId}>{tm("page_register", "How the guide talks to you:")}</span>
        <div role="radiogroup" aria-labelledby={labelId} className="inline-flex items-center">
          {REGISTERS.map((r, i) => (
            <span key={r} className="inline-flex items-center">
              {i > 0 && <span aria-hidden className="text-muted-foreground/50">·</span>}
              <button
                type="button"
                role="radio"
                aria-checked={register === r}
                aria-describedby={register === r ? hintId : undefined}
                title={tm(`register_hint_${r}`, HINT_FALLBACK[r])}
                onClick={() => onChange(r)}
                className={cn(
                  // Small text, full 44px hit area.
                  "min-h-[44px] px-1.5 rounded focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-primary/40",
                  register === r ? "font-medium text-foreground underline underline-offset-4 decoration-primary/40" : "hover:text-foreground"
                )}
              >
                {tm(`register_${r}`, r)}
              </button>
            </span>
          ))}
        </div>
      </div>
      <p id={hintId} className="-mt-2 text-[11px] text-muted-foreground/80">
        {tm(`register_hint_${register}`, HINT_FALLBACK[register])}
      </p>
    </div>
  );
};

export default MmVoiceSwitch;
